import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Edit, Plus, Trash2 } from 'lucide-react';
import { ContractStatusBadge, ContractDeleteDialog, ContractFilters } from '../components';
import { useContracts, useDeleteContract } from '../hooks';
import { Contract, ContractStatus } from '../utils/types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Pagination, DataTable } from '@/shared/widgets';
import { useDebounce } from '@/shared/hooks/useDebounce';

export const ContractsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Estados de filtros
  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [statusFilter, setStatusFilter] = useState<ContractStatus | 'all'>(
    (searchParams.get('status') as ContractStatus | 'all') ?? 'all'
  );
  const [includeDeleted, setIncludeDeleted] = useState(searchParams.get('deleted') !== 'false');
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get('page') ?? '1') || 1
  );
  const [pageSize, setPageSize] = useState(
    Number(searchParams.get('pageSize') ?? '10') || 10
  );

  const debouncedSearch = useDebounce(search, 400);

  // Estado do dialog de exclusão
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);

  // Hook para buscar contratos
  const {
    contracts,
    totalPages,
    totalItems,
    currentPage: currentPageFromApi,
    isLoading,
    error,
    queryResult,
  } = useContracts({
    page: currentPage,
    limit: pageSize,
    status: statusFilter,
    search: debouncedSearch,
    includeDeleted,
    sortBy: 'createdAt',
    order: 'desc',
  });


  // Atualiza query params quando filtros/paginação mudam
  useEffect(() => {
    const params = new URLSearchParams();

    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (!includeDeleted) params.set('deleted', 'false');
    if (currentPage > 1) params.set('page', String(currentPage));
    if (pageSize !== 10) params.set('pageSize', String(pageSize));

    setSearchParams(params, { replace: true });
  }, [debouncedSearch, statusFilter, includeDeleted, currentPage, pageSize, setSearchParams]);

  // Reseta página ao alterar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, includeDeleted, pageSize]);

  useEffect(() => {
    if (currentPageFromApi && currentPageFromApi !== currentPage) {
      setCurrentPage(currentPageFromApi);
    }
  }, [currentPageFromApi, currentPage]);

  // Atualiza pageSize se a API retornar um limite diferente
  useEffect(() => {
    const apiLimit = queryResult.data?.limit;
    if (typeof apiLimit === 'number' && apiLimit > 0 && apiLimit !== pageSize) {
      setPageSize(apiLimit);
    }
  }, [pageSize, queryResult.data?.limit]);

  // Hook para exclusão
  const { deleteContract, isLoading: isDeleting } = useDeleteContract();

  // Handlers
  const handleEdit = (contractId: string) => {
    navigate(`/contracts/${contractId}`);
  };

  const handleDelete = (contract: Contract) => {
    setContractToDelete(contract);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!contractToDelete) return;

    try {
      await deleteContract(contractToDelete.id);
      setDeleteDialogOpen(false);
      setContractToDelete(null);
    } catch (error) {
      // Erro já tratado no hook
      console.error('Erro ao excluir contrato:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: ptBR });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contratos</h1>
          <p className="text-muted-foreground">
            Gerencie os contratos com creators
          </p>
        </div>
        <Button onClick={() => navigate('/contracts/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Contrato
        </Button>
      </div>

      {/* Filtros */}
      <Card className='bg-white/[0.03] border-white/[0.06]'>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>Filtre os contratos por status, nome ou visualize excluídos</CardDescription>
        </CardHeader>
        <CardContent>
          <ContractFilters
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            includeDeleted={includeDeleted}
            onIncludeDeletedChange={setIncludeDeleted}
          />
        </CardContent>
      </Card>

      {/* Tabela */}
      <DataTable
        data={contracts}
        isLoading={isLoading}
        error={error}
        emptyTitle="Nenhum contrato encontrado"
        emptyDescription={(hasFilters) =>
          hasFilters
            ? 'Tente ajustar os filtros'
            : 'Comece criando um novo contrato'
        }
        emptyActionLabel="Novo Contrato"
        emptyActionOnClick={() => navigate('/contracts/new')}
        hasFilters={Boolean(search || statusFilter !== 'all')}
        errorMessage={`Erro ao carregar contratos: ${error?.message}`}
        columns={[
          {
            header: "Creator",
            cell: (contract) => (
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={contract.creator.avatar || undefined} />
                  <AvatarFallback>
                    {getInitials(contract.creator.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className={`font-medium ${contract.deletedAt ? 'opacity-50' : ''}`}>
                    {contract.creator.name}
                  </p>
                  {contract.creator.email && (
                    <p className="text-sm text-muted-foreground">
                      {contract.creator.email}
                    </p>
                  )}
                </div>
              </div>
            ),
          },
          {
            header: "Data de Início",
            cell: (contract) => (
              <span className={contract.deletedAt ? 'opacity-50' : ''}>
                {formatDate(contract.startDate)}
              </span>
            ),
          },
          {
            header: "Data de Fim",
            cell: (contract) => (
              <span className={contract.deletedAt ? 'opacity-50' : ''}>
                {formatDate(contract.endDate)}
              </span>
            ),
          },
          {
            header: "Status",
            cell: (contract) => (
              <div className="flex items-center gap-2">
                <ContractStatusBadge status={contract.status} />
                {contract.deletedAt && (
                  <Badge variant="destructive" className="bg-red-50 text-red-600 hover:bg-white">
                    Excluído
                  </Badge>
                )}
              </div>
            ),
          },
          {
            header: "Ações",
            cell: (contract) => (
              <div className="flex justify-end gap-2">
                {!contract.deletedAt && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(contract.id)}
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(contract)}
                      title="Excluir"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </>
                )}
              </div>
            ),
            className: "text-right",
          },
        ]}
      />
      {!isLoading && !error && totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          showPageSizeOptions
          pageSizeOptions={[10, 20, 50]}
          onPageSizeChange={(size) => {
            setCurrentPage(1);
            setPageSize(size);
          }}
          showTotalItems
          syncWithQueryParams={false}
        />
      )}

      {/* Dialog de confirmação de exclusão */}
      <ContractDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        creatorName={contractToDelete?.creator.name || ''}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};
